import { useState } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import InputField from 'dashboard/components/FormControls/InputField';
import PhoneInputField from 'dashboard/components/FormControls/PhoneInputField';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import * as Spacing from 'design-system/variables/spacing';
import { EMAIL_ADDRESS_REGEX } from 'lib/constants/regexes';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import { isMaxLength, isRequired, matchesRegexp, withMessage } from 'lib/utils/formValidation';

const validate = {
  'reference-id': [
    withMessage('Please limit your Reference ID to 255 characters', isMaxLength(255)),
  ],
  [PiiAttributes.NameFirst]: [withMessage('Please enter a first name', isRequired)],
  [PiiAttributes.NameLast]: [withMessage('Please enter a last name', isRequired)],
  [PiiAttributes.EmailAddress]: [
    withMessage('Please enter an email address', isRequired),
    withMessage('Please enter a valid email address', matchesRegexp(EMAIL_ADDRESS_REGEX)),
  ],
  [PiiAttributes.PhoneNumber]: [withMessage('Please enter a phone number', isRequired)],
  [PiiAttributes.AddressCity]: [withMessage('Please enter a city', isRequired)],
  [PiiAttributes.AddressSubdivision]: [
    withMessage('Please enter a state, region or subdivision', isRequired),
  ],
};

interface FormCreateReportSocialMediaProps {
  hideReferenceId: boolean;
  handleCancel: () => void;
  readOnly?: boolean;
}

enum CreateStrategy {
  EmailAddress = 'EmailAddress',
  PhoneNumber = 'PhoneNumber',
  NameAndCitySubdivision = 'NameAndCitySubdivision',
}

const StyledAccordion = styled(Accordion)`
  margin-bottom: ${Spacing.BASE}px;
`;

const FormCreateReportSocialMedia = ({
  submitting,
  hideReferenceId,
  handleSubmit,
  handleCancel,
  readOnly,
}: FormCreateReportSocialMediaProps &
  InjectedFormProps<FormCreateReportSocialMediaData, FormCreateReportSocialMediaProps>) => {
  const [strategy, setStrategy] = useState(CreateStrategy.EmailAddress);

  return (
    <>
      <StyledAccordion
        title='With Email Address'
        dataTest='email-address'
        onTitleClick={setStrategy.bind(null, CreateStrategy.EmailAddress)}
        overrideExpanded={strategy === CreateStrategy.EmailAddress}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            {!hideReferenceId && (
              <Field
                id='create-report-social-media__reference-id'
                label='Reference ID (Optional)'
                name='reference-id'
                disabled={submitting || readOnly}
                component={InputField}
                validate={validate['reference-id']}
                data-test='create-report-social-media__reference-id'
              />
            )}
            <Field
              id='create-report-social-media__email_address'
              label='Email Address'
              name={PiiAttributes.EmailAddress}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              validate={validate[PiiAttributes.EmailAddress]}
              placeholder='Email Address'
              data-test='create-report-social-media__email-address'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-social-media__cancel'
              data-test='create-report-social-media__cancel'
              disabled={submitting || readOnly}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-social-media__submit'
              data-test='create-report-social-media__submit'
              disabled={submitting || readOnly}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
      <StyledAccordion
        title='With Phone Number'
        dataTest='phone-number'
        onTitleClick={setStrategy.bind(null, CreateStrategy.PhoneNumber)}
        overrideExpanded={strategy === CreateStrategy.PhoneNumber}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            {!hideReferenceId && (
              <Field
                id='create-report-social-media__reference-id'
                label='Reference ID (Optional)'
                name='reference-id'
                disabled={submitting || readOnly}
                component={InputField}
                validate={validate['reference-id']}
                data-test='create-report-social-media__reference-id'
              />
            )}
            <Field
              id='create-report-social-media__address'
              label='Phone Number'
              name={PiiAttributes.PhoneNumber}
              type='text'
              disabled={submitting || readOnly}
              component={PhoneInputField}
              validate={validate[PiiAttributes.PhoneNumber]}
              placeholder='Phone Number'
              data-test='create-report-social-media__phone-number'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-social-media__cancel'
              data-test='create-report-social-media__cancel'
              disabled={submitting || readOnly}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-social-media__submit'
              data-test='create-report-social-media__submit'
              disabled={submitting || readOnly}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
      <StyledAccordion
        title='With Name and Location'
        dataTest='name-and-location'
        onTitleClick={setStrategy.bind(null, CreateStrategy.NameAndCitySubdivision)}
        overrideExpanded={strategy === CreateStrategy.NameAndCitySubdivision}
      >
        <Form onSubmit={handleSubmit}>
          <FormSection cols={1}>
            {!hideReferenceId && (
              <Field
                id='create-report-social-media__reference-id'
                label='Reference ID (Optional)'
                name='reference-id'
                disabled={submitting || readOnly}
                component={InputField}
                validate={validate['reference-id']}
                data-test='create-report-social-media__reference-id'
              />
            )}
          </FormSection>
          <FormSection cols={2}>
            <Field
              id='create-report-social-media__name-first'
              label='First name'
              name={PiiAttributes.NameFirst}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              placeholder='First name'
              validate={validate[PiiAttributes.NameFirst]}
              data-test='create-report-social-media__name-first'
            />
            <Field
              id='create-report-social-media__name-middle'
              label='Middle name (Optional)'
              name={PiiAttributes.NameMiddle}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              placeholder='Middle name'
              data-test='create-report-social-media__name-middle'
            />
            <Field
              id='create-report-social-media__name-last'
              label='Last name'
              name={PiiAttributes.NameLast}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              placeholder='Last name'
              validate={validate[PiiAttributes.NameLast]}
              data-test='create-report-social-media__name-last'
            />
          </FormSection>
          <FormSection cols={2}>
            <Field
              id='create-report-social-media__address-city'
              label='City'
              name={PiiAttributes.AddressCity}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              placeholder='City or Locality'
              validate={validate[PiiAttributes.AddressCity]}
              data-test='create-report-social-media__address-city'
            />
            <Field
              id='create-report-social-media__address-subdivision'
              label='State'
              name={PiiAttributes.AddressSubdivision}
              type='text'
              disabled={submitting || readOnly}
              component={InputField}
              placeholder='State, Region, or Subdivision'
              validate={validate[PiiAttributes.AddressSubdivision]}
              data-test='create-report-social-media__address-subdivision'
            />
          </FormSection>
          <HStack equal={true}>
            <Button
              variant='basic'
              id='create-report-social-media__cancel'
              data-test='create-report-social-media__cancel'
              disabled={submitting || readOnly}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              id='create-report-social-media__submit'
              data-test='create-report-social-media__submit'
              disabled={submitting || readOnly}
              isPending={submitting}
            >
              Create Report
            </Button>
          </HStack>
        </Form>
      </StyledAccordion>
    </>
  );
};

export interface FormCreateReportSocialMediaData {
  'reference-id'?: string;
  [PiiAttributes.NameFirst]?: string;
  [PiiAttributes.NameMiddle]?: string;
  [PiiAttributes.NameLast]?: string;
  [PiiAttributes.AddressSubdivision]?: string;
  [PiiAttributes.AddressPostalCode]?: string;
  [PiiAttributes.AddressCity]?: string;
  [PiiAttributes.EmailAddress]?: string;
  [PiiAttributes.PhoneNumber]?: string;
}

export const FORM_CREATE_REPORT_SOCIAL_MEDIA_NAME = 'formCreateReportSocialMediaData';

export default reduxForm<FormCreateReportSocialMediaData, FormCreateReportSocialMediaProps>({
  form: FORM_CREATE_REPORT_SOCIAL_MEDIA_NAME,
})(FormCreateReportSocialMedia);
